import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import DraggableModal from "../DraggableModal";
import SearchSelect from "../SearchSelect";
import EmailDialog from "../EmailDialog";
import { defaultOpenSeasonIds, buildSeasonFilterOptions, seasonFilterPlaceholder, resolveSeasonId } from "../seasonFilter";
import { htmlToPdfBlob, safeFilename } from "../../pdfRenderer";
import { loadLS, escHtml, fmtMoney, fmtInt, fmtRangeShort, isOrganicCategory, commodityFor, COMMODITY_LABELS, COMMODITY_ORDER, monthKey, monthLabel, monthsBetween, reportStyles, reportHeader, reportFooter, openPreviewWindow, downloadBlob } from "./auditCommon";
import { downloadXlsx, s as xs, num2 } from "../xlsxExport";
import { loadFromSupabase } from "../../supabaseDB";
import { appendDisclosureByAccount } from "../emailHelpers";

/* ============================================================================
 * Mass Balance Report — Inbounds-Outbounds Summary
 *
 * Inbounds come from gfi-shipments; we count cases on every shipment
 * line whose category is Organic. Outbounds come from gfi-invoices;
 * same filter, but we also track sale_value (cases × salePrice) and
 * commission per the line's commissionRate (with fallback to the
 * invoice-level rate). Profit shown to the auditor is commission only —
 * the spec is explicit that price markup is NEVER included.
 *
 * Grouping: per commodity (BLK / BLU / RSP / OTHER organic). Each
 * group renders a yearly table on the left and a monthly breakdown
 * on the right. A grand-total Inbounds vs Outbounds vs Balance row
 * closes the report.
 *
 * Selectors: date range, season multi (default Open), commodity
 * (All organic / per-commodity isolation).
 * ========================================================================= */

const T = {
  en: {
    title: "Mass Balance Report",
    sub: "Inbounds-Outbounds Summary",
    from: "From", to: "To", commodity: "Commodity", allOrganic: "All organic",
    season: "Season",
    inbounds: "Inbounds Summary (Yearly)",
    inboundsMonthly: "Inbounds Summary (Monthly)",
    outbounds: "Outbounds Summary (Yearly)",
    outboundsMonthly: "Outbounds Summary (Monthly)",
    item: "Item", qty: "Qty", sales: "Sales", month: "Month", total: "Total",
    totalInbounds: "Total Inbounds (Cases)",
    totalOutbounds: "Total Outbounds (Cases)",
    profit: "Profit (Commission Only)",
    balance: "Balance (Inbound − Outbound)",
    noData: "No data in the selected range.",
    btnPreview: "Preview", btnPdf: "Print / PDF", btnEmail: "Email", btnExcel: "Export Excel",
    close: "Close", sending: "Generating PDF...",
    emailSubject: "Mass Balance Report",
    season_yyyy: "Season",
  },
  es: {
    title: "Reporte de Balance de Masas",
    sub: "Resumen de Entradas — Salidas",
    from: "Desde", to: "Hasta", commodity: "Commodity", allOrganic: "Todos orgánicos",
    season: "Temporada",
    inbounds: "Resumen de Entradas (Anual)",
    inboundsMonthly: "Resumen de Entradas (Mensual)",
    outbounds: "Resumen de Salidas (Anual)",
    outboundsMonthly: "Resumen de Salidas (Mensual)",
    item: "Producto", qty: "Cajas", sales: "Ventas", month: "Mes", total: "Total",
    totalInbounds: "Total Entradas (Cajas)",
    totalOutbounds: "Total Salidas (Cajas)",
    profit: "Ganancia (Solo Comisión)",
    balance: "Balance (Entradas − Salidas)",
    noData: "Sin datos en el rango seleccionado.",
    btnPreview: "Vista Previa", btnPdf: "Imprimir / PDF", btnEmail: "Enviar Correo", btnExcel: "Exportar Excel",
    close: "Cerrar", sending: "Generando PDF...",
    emailSubject: "Reporte de Balance de Masas",
    season_yyyy: "Temporada",
  },
};

const inp = { padding: "8px 10px", fontSize: 13, border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };
const btnPrimary = { padding: "8px 16px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 13, fontWeight: 700 };
const btnSecondary = { padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 500 };

/* ============================================================================
 * Data aggregation
 * ========================================================================= */
/* Effective shipment date — the auditor cares about when the goods
   ENTERED US customs, not when they left Mexico. Falls back through
   the available date fields so legacy / imported rows that only carry
   one of them still classify. */
function shipmentDate(s) {
  return (s && (s.dateArrival || s.dateDeparture || s.date)) || "";
}

/* Organic detection. The shipment line's own category is the primary
   signal, but a non-trivial fraction of historical imports came in
   with an empty `category` field on the line — those rows would
   otherwise drop out of the report entirely. Fallback: resolve the
   line's productId against the loaded products catalog and use the
   product's category. */
function isOrganicLine(line, productsById) {
  if (!line) return false;
  if (isOrganicCategory(line.category)) return true;
  var p = line.productId && productsById ? productsById[line.productId] : null;
  return !!(p && isOrganicCategory(p.category));
}

function buildAggregate(opts, data) {
  /* opts: { dateFrom, dateTo, seasonIds, commodityFilter, lang }
     data: { shipments, invoices, products, seasons }
     Returns: {
       perCommodity: { BLK|BLU|RSP|OTHER: {
         items: { itemKey: { name, cases } },  // yearly inbounds table
         monthly: { ym: cases },                 // monthly inbounds
         out_items: { itemKey: { name, cases, sales } },
         out_monthly: { ym: { cases, sales } },
         commission: number,
       }},
       grand: { inbounds: cases, outbounds: cases, sales, commission, balance },
       monthsList: ["2026-01", ...],
     } */
  var seasons   = (data && data.seasons)   || [];
  var products  = (data && data.products)  || [];
  var shipments = (data && data.shipments) || [];
  var invoices  = (data && data.invoices)  || [];
  var productsById = {};
  products.forEach(function (p) { productsById[p.id] = p; });

  /* Range filters. seasonIds === undefined means "no season filter
     applied" (caller can opt out for a pure date range). Empty array
     means "no seasons selected" → no records pass. */
  var inRange = function (dateStr) {
    if (!dateStr) return false;
    if (opts.dateFrom && dateStr < opts.dateFrom) return false;
    if (opts.dateTo   && dateStr > opts.dateTo)   return false;
    return true;
  };
  var inSeason = function (dateStr, sid) {
    if (!opts.seasonIds) return true;
    var rsid = resolveSeasonId(seasons, dateStr, sid);
    return opts.seasonIds.indexOf(rsid) >= 0;
  };
  var inCommodity = function (com) {
    if (!opts.commodityFilter || opts.commodityFilter === "ALL") return true;
    return com === opts.commodityFilter;
  };
  var inShipper = function (shipperName) {
    if (!opts.shipperFilter || opts.shipperFilter === "ALL") return true;
    return String(shipperName || "") === String(opts.shipperFilter);
  };
  var inProduct = function (line) {
    if (!opts.productFilter || opts.productFilter === "ALL") return true;
    var d = (line && (line.productDescription || line.productName)) || "";
    return String(d) === String(opts.productFilter);
  };

  var perCommodity = {};
  COMMODITY_ORDER.forEach(function (c) {
    perCommodity[c] = { items: {}, monthly: {}, out_items: {}, out_monthly: {}, commission: 0 };
  });

  /* INBOUNDS */
  shipments.forEach(function (s) {
    var dateStr = shipmentDate(s);
    if (!inRange(dateStr)) return;
    if (!inSeason(dateStr, s.seasonId)) return;
    if (!inShipper(s.shipperName || s.shipper)) return;
    (s.lines || []).forEach(function (line) {
      if (!isOrganicLine(line, productsById)) return;
      if (!inProduct(line)) return;
      var com = commodityFor(line.productId || line.productName, productsById);
      if (!inCommodity(com)) return;
      var cases = parseInt(line.cases, 10) || 0;
      if (cases <= 0) return;
      var bucket = perCommodity[com];
      var itemKey = (line.productId || "") + "|" + (line.productName || "");
      var itemName = line.productDescription || line.productName || "—";
      if (!bucket.items[itemKey]) bucket.items[itemKey] = { name: itemName, cases: 0 };
      bucket.items[itemKey].cases += cases;
      var ym = monthKey(dateStr);
      bucket.monthly[ym] = (bucket.monthly[ym] || 0) + cases;
    });
  });

  /* Lookup table for invoice → shipment date resolution below.
     LOT 57969 had pallets invoiced across February + March, but the
     fruit arrived US customs on a single date — classifying the
     outbounds by invoiceDate produced a 240-case mass-balance gap
     in February that didn't exist. Match the auditor's mental
     model: an outbound counts in the month its INBOUND was received,
     not when GFI happened to issue the bill. */
  var shipmentByLot = {};
  var shipmentById  = {};
  shipments.forEach(function (s) {
    if (!s) return;
    if (s.lot != null && s.lot !== "") shipmentByLot[String(s.lot)] = s;
    if (s.id  != null) shipmentById[String(s.id)] = s;
    if (s._uuid) shipmentById[String(s._uuid)] = s;
  });
  /* OUTBOUNDS — classify by linked-shipment arrival date, not by
     invoice date. Falls back to inv.invoiceDate when the link can't
     be resolved (legacy invoices without lot / shipmentId). */
  invoices.forEach(function (inv) {
    var linkedShipment = null;
    if (inv.shipmentId) linkedShipment = shipmentById[String(inv.shipmentId)] || null;
    if (!linkedShipment && inv.lot != null && inv.lot !== "") linkedShipment = shipmentByLot[String(inv.lot)] || null;
    if (!linkedShipment) {
      /* Lot might be stamped on the first line only (older invoices). */
      var lotFromLines = (inv.lines || []).map(function (l) { return l && l.lot; }).filter(Boolean)[0];
      if (lotFromLines) linkedShipment = shipmentByLot[String(lotFromLines)] || null;
    }
    var dateStr = (linkedShipment && shipmentDate(linkedShipment)) || inv.invoiceDate || inv.date;
    if (!inRange(dateStr)) return;
    if (!inSeason(dateStr, inv.seasonId)) return;
    var outShipper = (linkedShipment && (linkedShipment.shipperName || linkedShipment.shipper)) || inv.shipperName || "";
    if (!inShipper(outShipper)) return;
    var invRate = parseFloat(inv.commissionRate) || 0;
    (inv.lines || []).forEach(function (line) {
      if (!isOrganicLine(line, productsById)) return;
      if (!inProduct(line)) return;
      var com = commodityFor(line.productId || line.productName, productsById);
      if (!inCommodity(com)) return;
      var cases = parseInt(line.cases, 10) || 0;
      if (cases <= 0) return;
      var bucket = perCommodity[com];
      var itemKey = (line.productId || "") + "|" + (line.productName || "");
      var itemName = line.productDescription || line.productName || "—";
      /* Sale value = cases × salePrice (no fee/credit deductions —
         the auditor wants the gross top-line). */
      var sales = cases * (parseFloat(line.salePrice) || 0);
      var rate = parseFloat(line.commissionRate);
      if (isNaN(rate)) rate = invRate;
      var commission = sales * (rate / 100);
      if (!bucket.out_items[itemKey]) bucket.out_items[itemKey] = { name: itemName, cases: 0, sales: 0 };
      bucket.out_items[itemKey].cases += cases;
      bucket.out_items[itemKey].sales += sales;
      var ym = monthKey(dateStr);
      if (!bucket.out_monthly[ym]) bucket.out_monthly[ym] = { cases: 0, sales: 0 };
      bucket.out_monthly[ym].cases += cases;
      bucket.out_monthly[ym].sales += sales;
      bucket.commission += commission;
    });
  });

  /* Months list spanning the active range — used to render an even
     monthly axis on both sides. */
  var monthsList = monthsBetween(opts.dateFrom, opts.dateTo || opts.dateFrom);

  var grand = { inbounds: 0, outbounds: 0, sales: 0, commission: 0, balance: 0 };
  COMMODITY_ORDER.forEach(function (c) {
    var b = perCommodity[c];
    Object.keys(b.items).forEach(function (k) { grand.inbounds += b.items[k].cases; });
    Object.keys(b.out_items).forEach(function (k) { grand.outbounds += b.out_items[k].cases; grand.sales += b.out_items[k].sales; });
    grand.commission += b.commission;
  });
  grand.balance = grand.inbounds - grand.outbounds;

  return { perCommodity: perCommodity, grand: grand, monthsList: monthsList };
}

/* ============================================================================
 * HTML builder
 * ========================================================================= */
function buildMassBalanceHTML(agg, opts, audit, lang) {
  var t = T[lang === "es" ? "es" : "en"];
  var labels = COMMODITY_LABELS[lang === "es" ? "es" : "en"];
  var rangeStr = fmtRangeShort(opts.dateFrom, opts.dateTo);
  var commodityLabel = opts.commodityFilter === "ALL"
    ? t.allOrganic
    : (labels[opts.commodityFilter] || opts.commodityFilter);
  /* Subtitle no longer carries the season string — the audit banner
     in the header (rendered by reportHeader's 4th arg) handles that.
     Subtitle stays as the commodity filter label. */
  var subtitleBits = [commodityLabel];
  if (opts.shipperFilter && opts.shipperFilter !== "ALL") subtitleBits.push((lang === "es" ? "Shipper: " : "Shipper: ") + opts.shipperFilter);
  if (opts.productFilter && opts.productFilter !== "ALL") subtitleBits.push(opts.productFilter);
  var subtitle = subtitleBits.join("  ·  ");

  var h = '';
  h += '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + escHtml(t.title) + '</title>';
  h += '<style>' + reportStyles() + '</style></head><body><div class="page">';
  h += reportHeader(t.title, subtitle, rangeStr, audit);

  var anyData = false;
  /* If filtered to a single commodity, only render that one. */
  var activeList = opts.commodityFilter === "ALL"
    ? COMMODITY_ORDER
    : COMMODITY_ORDER.filter(function (c) { return c === opts.commodityFilter; });

  activeList.forEach(function (com) {
    var b = agg.perCommodity[com];
    var itemKeys = Object.keys(b.items);
    var outItemKeys = Object.keys(b.out_items);
    if (itemKeys.length === 0 && outItemKeys.length === 0) return;
    anyData = true;

    h += '<div class="commodity-title">' + escHtml(labels[com] || com) + '</div>';

    /* INBOUNDS */
    h += '<div class="section-title">' + escHtml(t.inbounds) + '   ·   ' + escHtml(t.inboundsMonthly) + '</div>';
    h += '<div class="split">';
    /* Left — yearly table */
    h += '<div><table><thead><tr><th>' + escHtml(t.item) + '</th><th style="text-align:right;width:80px">' + escHtml(t.qty) + '</th></tr></thead><tbody>';
    var inTotal = 0;
    if (itemKeys.length === 0) {
      h += '<tr><td colspan="2" style="text-align:center;color:#888;font-style:italic">' + escHtml(t.noData) + '</td></tr>';
    } else {
      itemKeys.sort(function (a, b2) { return (b.items[a].name || "").localeCompare(b.items[b2].name || ""); });
      itemKeys.forEach(function (k) {
        var it = b.items[k]; inTotal += it.cases;
        h += '<tr><td>' + escHtml(it.name) + '</td><td style="text-align:right;font-family:\'JetBrains Mono\',monospace">' + escHtml(fmtInt(it.cases)) + '</td></tr>';
      });
      h += '<tr class="total"><td>' + escHtml(t.total) + '</td><td style="text-align:right;font-family:\'JetBrains Mono\',monospace">' + escHtml(fmtInt(inTotal)) + '</td></tr>';
    }
    h += '</tbody></table></div>';
    /* Right — monthly table */
    h += '<div><table><thead><tr><th>' + escHtml(t.month) + '</th><th style="text-align:right;width:80px">' + escHtml(t.qty) + '</th></tr></thead><tbody>';
    var mTotal = 0;
    var monthKeysIn = Object.keys(b.monthly).sort();
    if (monthKeysIn.length === 0) {
      h += '<tr><td colspan="2" style="text-align:center;color:#888;font-style:italic">' + escHtml(t.noData) + '</td></tr>';
    } else {
      monthKeysIn.forEach(function (ym) {
        var v = b.monthly[ym]; mTotal += v;
        h += '<tr><td>' + escHtml(monthLabel(ym, lang)) + '</td><td style="text-align:right;font-family:\'JetBrains Mono\',monospace">' + escHtml(fmtInt(v)) + '</td></tr>';
      });
      h += '<tr class="total"><td>' + escHtml(t.total) + '</td><td style="text-align:right;font-family:\'JetBrains Mono\',monospace">' + escHtml(fmtInt(mTotal)) + '</td></tr>';
    }
    h += '</tbody></table></div>';
    h += '</div>';

    /* OUTBOUNDS */
    h += '<div class="section-title" style="margin-top:14px">' + escHtml(t.outbounds) + '   ·   ' + escHtml(t.outboundsMonthly) + '</div>';
    h += '<div class="split">';
    h += '<div><table><thead><tr><th>' + escHtml(t.item) + '</th><th style="text-align:right;width:90px">' + escHtml(t.qty) + '</th></tr></thead><tbody>';
    var outCT = 0;
    if (outItemKeys.length === 0) {
      h += '<tr><td colspan="2" style="text-align:center;color:#888;font-style:italic">' + escHtml(t.noData) + '</td></tr>';
    } else {
      outItemKeys.sort(function (a, b2) { return (b.out_items[a].name || "").localeCompare(b.out_items[b2].name || ""); });
      outItemKeys.forEach(function (k) {
        var it = b.out_items[k]; outCT += it.cases;
        h += '<tr><td>' + escHtml(it.name) + '</td><td style="text-align:right;font-family:\'JetBrains Mono\',monospace">' + escHtml(fmtInt(it.cases)) + '</td></tr>';
      });
      h += '<tr class="total"><td>' + escHtml(t.total) + '</td><td style="text-align:right;font-family:\'JetBrains Mono\',monospace">' + escHtml(fmtInt(outCT)) + '</td></tr>';
    }
    h += '</tbody></table></div>';
    h += '<div><table><thead><tr><th>' + escHtml(t.month) + '</th><th style="text-align:right;width:90px">' + escHtml(t.qty) + '</th></tr></thead><tbody>';
    var moCT = 0;
    var monthKeysOut = Object.keys(b.out_monthly).sort();
    if (monthKeysOut.length === 0) {
      h += '<tr><td colspan="2" style="text-align:center;color:#888;font-style:italic">' + escHtml(t.noData) + '</td></tr>';
    } else {
      monthKeysOut.forEach(function (ym) {
        var v = b.out_monthly[ym]; moCT += v.cases;
        h += '<tr><td>' + escHtml(monthLabel(ym, lang)) + '</td><td style="text-align:right;font-family:\'JetBrains Mono\',monospace">' + escHtml(fmtInt(v.cases)) + '</td></tr>';
      });
      h += '<tr class="total"><td>' + escHtml(t.total) + '</td><td style="text-align:right;font-family:\'JetBrains Mono\',monospace">' + escHtml(fmtInt(moCT)) + '</td></tr>';
    }
    h += '</tbody></table></div>';
    h += '</div>';
  });

  if (!anyData) {
    h += '<div class="section"><div style="padding:24px;text-align:center;color:#888;font-style:italic;border:1px dashed #d1d5db;border-radius:4px">' + escHtml(t.noData) + '</div></div>';
  }

  /* Grand totals + balance. */
  h += '<div class="section" style="margin-top:18px">';
  h += '<div class="balance-row"><span>' + escHtml(t.totalInbounds) + '</span><span style="font-family:\'JetBrains Mono\',monospace">' + escHtml(fmtInt(agg.grand.inbounds)) + '</span></div>';
  h += '<div class="balance-row"><span>' + escHtml(t.totalOutbounds) + '</span><span style="font-family:\'JetBrains Mono\',monospace">' + escHtml(fmtInt(agg.grand.outbounds)) + '</span></div>';
  var balStyle = agg.grand.balance === 0 ? '' : 'border-color:#DC2626;background:rgba(220,38,38,0.06);color:#991B1B';
  h += '<div class="balance-row" style="' + balStyle + '"><span>' + escHtml(t.balance) + '</span><span style="font-family:\'JetBrains Mono\',monospace">' + escHtml(fmtInt(agg.grand.balance)) + '</span></div>';
  h += '</div>';

  h += reportFooter(audit, lang);
  h += '</div></body></html>';
  return h;
}

/* ============================================================================
 * Excel export
 * ========================================================================= */
function downloadMassBalanceXLSX(agg, opts, audit, lang) {
  var ES = lang === "es";
  var labels = COMMODITY_LABELS[ES ? "es" : "en"];
  var sheets = {};
  /* Sheet 1 — Summary */
  var summary = [{
    [ES ? "Auditoría" : "Audit"]: audit && audit.name || "",
    [ES ? "Auditor"  : "Auditor"]: audit && audit.auditorName || "",
    [ES ? "Desde"    : "From"]:  opts.dateFrom || "",
    [ES ? "Hasta"    : "To"]:    opts.dateTo || "",
    [ES ? "Total Entradas (Cajas)" : "Total Inbounds (Cases)"]:  agg.grand.inbounds,
    [ES ? "Total Salidas (Cajas)"  : "Total Outbounds (Cases)"]: agg.grand.outbounds,
    [ES ? "Balance (Cajas)": "Balance (Cases)"]: agg.grand.balance,
  }];
  sheets[ES ? "Resumen" : "Summary"] = summary;

  /* Sheet 2 — Inbounds by line (commodity + product + cases). */
  var inRows = [];
  COMMODITY_ORDER.forEach(function (com) {
    var b = agg.perCommodity[com];
    Object.keys(b.items).forEach(function (k) {
      var it = b.items[k];
      var row = {};
      row[ES ? "Commodity" : "Commodity"] = labels[com] || com;
      row[ES ? "Producto"  : "Product"]   = xs(it.name);
      row[ES ? "Cajas"     : "Cases"]     = it.cases;
      inRows.push(row);
    });
  });
  sheets[ES ? "Entradas" : "Inbounds"] = inRows.length ? inRows : [{}];

  /* Sheet 3 — Outbounds by line. */
  var outRows = [];
  COMMODITY_ORDER.forEach(function (com) {
    var b = agg.perCommodity[com];
    Object.keys(b.out_items).forEach(function (k) {
      var it = b.out_items[k];
      var row = {};
      row[ES ? "Commodity" : "Commodity"] = labels[com] || com;
      row[ES ? "Producto"  : "Product"]   = xs(it.name);
      row[ES ? "Cajas"     : "Cases"]     = it.cases;
      outRows.push(row);
    });
  });
  sheets[ES ? "Salidas" : "Outbounds"] = outRows.length ? outRows : [{}];

  /* Sheet 4 — Monthly Inbounds. */
  var monIn = [];
  COMMODITY_ORDER.forEach(function (com) {
    var b = agg.perCommodity[com];
    Object.keys(b.monthly).sort().forEach(function (ym) {
      var row = {};
      row[ES ? "Commodity" : "Commodity"] = labels[com] || com;
      row[ES ? "Mes"       : "Month"]     = ym;
      row[ES ? "Cajas"     : "Cases"]     = b.monthly[ym];
      monIn.push(row);
    });
  });
  sheets[ES ? "Entradas (Mensual)" : "Inbounds (Monthly)"] = monIn.length ? monIn : [{}];

  /* Sheet 5 — Monthly Outbounds. */
  var monOut = [];
  COMMODITY_ORDER.forEach(function (com) {
    var b = agg.perCommodity[com];
    Object.keys(b.out_monthly).sort().forEach(function (ym) {
      var v = b.out_monthly[ym];
      var row = {};
      row[ES ? "Commodity" : "Commodity"] = labels[com] || com;
      row[ES ? "Mes"       : "Month"]     = ym;
      row[ES ? "Cajas"     : "Cases"]     = v.cases;
      monOut.push(row);
    });
  });
  sheets[ES ? "Salidas (Mensual)" : "Outbounds (Monthly)"] = monOut.length ? monOut : [{}];

  var fileBase = "Mass_Balance_" + (audit && audit.year || new Date().getFullYear());
  downloadXlsx(fileBase, ES ? "Resumen" : "Summary", sheets);
}

/* ============================================================================
 * Panel UI
 * ========================================================================= */
export default function MassBalancePanel({ lang, audit, onClose, onLogReport, initialParams }) {
  const t = T[lang === "es" ? "es" : "en"];
  /* Default range: full calendar year of the audit, falling back to
     audit start through today. */
  const defaultFrom = useMemo(() => {
    if (audit && audit.year) return audit.year + "-01-01";
    return new Date(new Date().getFullYear(), 0, 1).toISOString().substring(0, 10);
  }, [audit]);
  const defaultTo = useMemo(() => {
    if (audit && audit.year) return audit.year + "-12-31";
    return new Date().toISOString().substring(0, 10);
  }, [audit]);
  /* `initialParams` lets the registry's "Reopen" action restore the
     exact selectors a past report was built with. Fall back to the
     audit-derived defaults when no params are supplied (first time
     the user opens the panel). */
  const [dateFrom, setDateFrom] = useState((initialParams && initialParams.dateFrom) || defaultFrom);
  const [dateTo, setDateTo]     = useState((initialParams && initialParams.dateTo)   || defaultTo);
  const [commodity, setCommodity] = useState((initialParams && initialParams.commodity) || "ALL");
  const [shipperFilter, setShipperFilter] = useState((initialParams && initialParams.shipperFilter) || "ALL");
  const [productFilter, setProductFilter] = useState((initialParams && initialParams.productFilter) || "ALL");
  const [seasonIds, setSeasonIds] = useState(() =>
    (initialParams && Array.isArray(initialParams.seasonIds)) ? initialParams.seasonIds : defaultOpenSeasonIds()
  );
  const [busy, setBusy] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const lastBlobRef = useRef(null);
  const lastFilenameRef = useRef("");

  /* Source-of-truth load. The original implementation read only from
     localStorage (gfi-shipments etc.) which silently returned [] when
     the user opened the audit module without first hydrating those
     caches in the same session. Now we pull from Supabase, fall back
     to LS on network failure, and render a "Loading..." state until
     the first response settles. */
  const [data, setData] = useState({ shipments: null, invoices: null, products: null, seasons: null, suppliers: [] });
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      loadFromSupabase("shipments").catch(function () { return loadLS("gfi-shipments", []); }),
      loadFromSupabase("invoices").catch(function () { return loadLS("gfi-invoices", []); }),
      loadFromSupabase("products").catch(function () { return loadLS("gfi-products", []); }),
      loadFromSupabase("seasons").catch(function () { return loadLS("gfi-seasons", []); }),
      loadFromSupabase("suppliers").catch(function () { return loadLS("gfi-suppliers", []); }),
    ]).then(function (res) {
      if (cancelled) return;
      setData({ shipments: res[0] || [], invoices: res[1] || [], products: res[2] || [], seasons: res[3] || [], suppliers: res[4] || [] });
    });
    return function () { cancelled = true; };
  }, []);
  const ready = data.shipments !== null;

  const seasonGroups = useMemo(() => buildSeasonFilterOptions(lang), [lang]);
  const commodityOptions = useMemo(() => {
    const lab = COMMODITY_LABELS[lang === "es" ? "es" : "en"];
    return [{ value: "ALL", label: t.allOrganic }].concat(COMMODITY_ORDER.map(c => ({ value: c, label: lab[c] })));
  }, [lang, t.allOrganic]);

  /* Shipper options — only foreign (Mexico-based) shippers enter the
     organic import audit. Filter suppliers by supplier_type shipper
     and country Mexico. */
  const shipperOptions = useMemo(() => {
    const sups = (data && data.suppliers) || [];
    const mx = sups.filter(s => {
      const type = String(s.supplierType || s.supplier_type || "").toLowerCase();
      const country = String(s.country || "").toLowerCase();
      return type === "shipper" && (country === "mexico" || country === "méxico");
    });
    const names = mx.map(s => s.shortName || s.short_name || s.fullName || s.full_name).filter(Boolean);
    const uniq = Array.from(new Set(names)).sort();
    return [{ value: "ALL", label: (lang === "es" ? "Todos los shippers" : "All shippers") }]
      .concat(uniq.map(n => ({ value: n, label: n })));
  }, [data, lang]);

  /* Product (description) options — derived from the data, filtered by
     the currently selected commodity (cascade). If commodity is ALL,
     show every organic description. */
  const productOptions = useMemo(() => {
    const prods = (data && data.products) || [];
    const productsById = {};
    prods.forEach(p => { productsById[p.id] = p; productsById[p.legacy_id] = p; });
    const shipments = (data && data.shipments) || [];
    const invoices = (data && data.invoices) || [];
    const descs = {};
    const collect = (lines) => {
      (lines || []).forEach(line => {
        if (!isOrganicLine(line, productsById)) return;
        const com = commodityFor(line.productId || line.productName, productsById);
        if (commodity !== "ALL" && com !== commodity) return;
        const d = line.productDescription || line.productName;
        if (d) descs[d] = true;
      });
    };
    shipments.forEach(s => collect(s.lines));
    invoices.forEach(inv => collect(inv.lines));
    const uniq = Object.keys(descs).sort();
    return [{ value: "ALL", label: (lang === "es" ? "Todos los productos" : "All products") }]
      .concat(uniq.map(d => ({ value: d, label: d })));
  }, [data, commodity, lang]);

  /* When commodity changes, reset the description filter to ALL so the
     user never holds a description that doesn't belong to the new
     commodity (which would yield zero rows). */
  const handleCommodityChange = useCallback((v) => {
    setCommodity(v);
    setProductFilter("ALL");
  }, []);

  /* Re-aggregate whenever a selector OR the loaded data changes.
     `ready === false` (initial load) renders an empty agg so the
     selectors stay interactive while data streams in. */
  const agg = useMemo(() => buildAggregate({
    dateFrom, dateTo,
    seasonIds: seasonIds && seasonIds.length > 0 ? seasonIds : (seasonIds === null ? undefined : []),
    commodityFilter: commodity,
    shipperFilter: shipperFilter,
    productFilter: productFilter,
  }, ready ? data : { shipments: [], invoices: [], products: [], seasons: [] }), [dateFrom, dateTo, seasonIds, commodity, shipperFilter, productFilter, ready, data]);

  const buildHtml = useCallback(() => buildMassBalanceHTML(agg, { dateFrom, dateTo, commodityFilter: commodity, shipperFilter: shipperFilter, productFilter: productFilter }, audit, lang), [agg, dateFrom, dateTo, commodity, shipperFilter, productFilter, audit, lang]);
  const previewHtml = useMemo(() => buildHtml(), [buildHtml]);

  const handlePreview = () => openPreviewWindow(buildHtml());

  /* The params snapshot we attach to every log entry so the registry
     can rebuild this exact view on Reopen. */
  const currentParams = useMemo(() => ({ dateFrom, dateTo, commodity, seasonIds, shipperFilter, productFilter }), [dateFrom, dateTo, commodity, seasonIds, shipperFilter, productFilter]);

  const handlePdf = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const blob = await htmlToPdfBlob(buildHtml(), { orientation: "portrait" });
      const fn = safeFilename("Mass_Balance_" + (audit && audit.name || "")) + ".pdf";
      lastBlobRef.current = blob; lastFilenameRef.current = fn;
      downloadBlob(blob, fn);
      onLogReport({
        type: "mass_balance",
        title: (lang === "es" ? "PDF descargado" : "PDF downloaded") + " · " + fn,
        params: currentParams,
        blob: blob, filename: fn, mime: "application/pdf",
      });
    } catch (e) {
      console.error("[MassBalance] pdf failed", e);
      window.alert((lang === "es" ? "Error generando PDF: " : "PDF generation failed: ") + (e.message || String(e)));
    } finally { setBusy(false); }
  };

  const handleExcel = () => {
    try {
      downloadMassBalanceXLSX(agg, { dateFrom, dateTo, commodityFilter: commodity }, audit, lang);
      onLogReport({
        type: "mass_balance",
        title: (lang === "es" ? "Excel descargado" : "Excel downloaded"),
        params: currentParams,
      });
    } catch (e) {
      console.error("[MassBalance] xlsx failed", e);
      window.alert((lang === "es" ? "Error generando Excel: " : "Excel generation failed: ") + (e.message || String(e)));
    }
  };

  const handleEmail = () => setShowEmail(true);

  /* Build the EmailDialog props once when the user opens it. We provide
     a single doc kind (the report PDF) and a buildAttachments callback
     that re-renders the HTML and rasterises it. */
  const [emailProfile, setEmailProfile] = useState({});
  useEffect(() => {
    let alive = true;
    loadFromSupabase("company_profile")
      .then(function (rows) {
        if (!alive) return;
        var p = (rows || []).find(function (r) { return String(r.id) === "gfi"; }) || (rows || [])[0] || {};
        setEmailProfile(p);
      })
      .catch(function () {});
    return function () { alive = false; };
  }, []);
  const profile = emailProfile;
  const signatures = (profile && profile.emailSignatures) || [];
  const fromEmail  = (profile && (profile.outboundEmail || profile.email)) || "";
  /* 2026-09-30 — excludes Sweetleaf/automated accounts, same fix as
     OrganicAuditRegistry.js's own emailAccounts. */
  const emailAccounts = ((profile && profile.emailAccounts) || []).filter(function (a) { var n = (a.name || "").toLowerCase(); return n.indexOf("sweetleaf") < 0 && n.indexOf("automat") < 0; });

  return (
    <DraggableModal title={t.title + " · " + (audit && audit.name || "")} onClose={onClose} zIndex={10003}>
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
        {/* Selector strip */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.4fr 1fr", gap: 12, marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{t.from}</div>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inp} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{t.to}</div>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inp} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{t.season}</div>
            <SearchSelect multi groups={seasonGroups} value={seasonIds} onChange={setSeasonIds} placeholder={seasonFilterPlaceholder(lang)} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{t.commodity}</div>
            <SearchSelect options={commodityOptions} value={commodity} onChange={handleCommodityChange} placeholder={t.allOrganic} />
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{lang === "es" ? "Shipper" : "Shipper"}</div>
            <SearchSelect options={shipperOptions} value={shipperFilter} onChange={setShipperFilter} placeholder={lang === "es" ? "Todos los shippers" : "All shippers"} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{lang === "es" ? "Producto" : "Product"}</div>
            <SearchSelect options={productOptions} value={productFilter} onChange={setProductFilter} placeholder={lang === "es" ? "Todos los productos" : "All products"} />
          </div>
        </div>

        {!ready && (
          <div style={{ padding: "8px 12px", borderRadius: 6, background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.30)", color: "#92400E", fontSize: 12, marginBottom: 12 }}>
            {lang === "es" ? "Cargando datos de Supabase..." : "Loading data from Supabase..."}
          </div>
        )}

        {/* Compact live summary so user sees the numbers before printing. */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 18 }}>
          <SummaryCard label={t.totalInbounds}  value={fmtInt(agg.grand.inbounds)} />
          <SummaryCard label={t.totalOutbounds} value={fmtInt(agg.grand.outbounds)} />
          <SummaryCard label={t.balance}        value={fmtInt(agg.grand.balance)} highlight={agg.grand.balance !== 0} />
        </div>

        {/* Inline preview (small) — full HTML rendered in iframe. Keeps
            the modal interactive while showing the report layout. */}
        <div style={{ border: "1px solid var(--color-border)", borderRadius: 6, overflow: "hidden", background: "#fff", height: 360 }}>
          <iframe
            title="mass-balance-preview"
            srcDoc={previewHtml}
            style={{ width: "100%", height: "100%", border: 0 }}
          />
        </div>
      </div>
      <div style={{ padding: "12px 22px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{busy ? t.sending : ""}</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} style={btnSecondary}>{t.close}</button>
          <button onClick={handleExcel}   style={btnSecondary}>{t.btnExcel}</button>
          <button onClick={handlePreview} style={btnSecondary}>{t.btnPreview}</button>
          <button onClick={handleEmail}   style={btnSecondary}>{t.btnEmail}</button>
          <button onClick={handlePdf}     style={btnPrimary} disabled={busy}>{t.btnPdf}</button>
        </div>
      </div>

      {showEmail && (
        <EmailDialog
          lang={lang}
          title={t.btnEmail}
          recipients={(audit && audit.auditorEmail) ? [{ name: audit.auditorName || "", email: audit.auditorEmail, checked: true }] : []}
          docOptions={[{ key: "report", label: t.title, checked: true }]}
          defaultSubject={t.emailSubject + " — " + (audit && audit.name || "")}
          defaultBody={(lang === "es"
            ? "Adjunto el reporte de Balance de Masas para la auditoría " + (audit && audit.name || "") + "."
            : "Attached is the Mass Balance report for audit " + (audit && audit.name || "") + ".")}
          fromEmail={fromEmail}
          emailAccounts={emailAccounts}
          signatures={signatures}
          postProcessHtmlBody={(html, resolvedFrom) => appendDisclosureByAccount(html, resolvedFrom, profile, null)}
          buildAttachments={async () => {
            const blob = await htmlToPdfBlob(buildHtml(), { orientation: "portrait" });
            const fn = safeFilename("Mass_Balance_" + (audit && audit.name || "")) + ".pdf";
            lastBlobRef.current = blob; lastFilenameRef.current = fn;
            return [{ filename: fn, blob: blob, mimeType: "application/pdf" }];
          }}
          onSent={() => {
            const to = (audit && audit.auditorEmail) ? [audit.auditorEmail] : [];
            onLogReport({
              type: "mass_balance",
              title: lang === "es" ? "Enviado por correo" : "Sent by email",
              recipients: to,
              params: currentParams,
              blob: lastBlobRef.current, filename: lastFilenameRef.current, mime: "application/pdf",
            });
            setShowEmail(false);
          }}
          onClose={() => setShowEmail(false)}
        />
      )}
    </DraggableModal>
  );
}

function SummaryCard({ label, value, highlight }) {
  return (
    <div style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid " + (highlight ? "#DC2626" : "var(--color-border)"), background: highlight ? "rgba(220,38,38,0.06)" : "var(--color-bg-card)" }}>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-muted)", marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 700, color: highlight ? "#991B1B" : "var(--color-text)", fontFamily: "'JetBrains Mono', monospace" }}>{value}</div>
    </div>
  );
}
